import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { featureLimitsAPI, FEATURE_NAMES } from '../../apis/featureLimits';

const FeatureLimitsContext = createContext();

export const useFeatureLimits = () => {
  const context = useContext(FeatureLimitsContext);
  if (!context) {
    throw new Error('useFeatureLimits must be used within a FeatureLimitsProvider');
  }
  return context;
};

export const FeatureLimitsProvider = ({ children }) => {
  const [userLimits, setUserLimits] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Make userId reactive by using state and checking localStorage
  const [userId, setUserId] = useState(() => localStorage.getItem('UserID'));

  // Load user limits and subscription status - memoized with useCallback
  const loadUserData = useCallback(async () => {
    const currentUserId = localStorage.getItem('UserID');
    if (!currentUserId) {
      // Clear data if user logged out
      setUserLimits([]);
      setSubscriptionStatus(null);
      setUserId(null);
      return;
    }

    // Update userId state if it changed
    setUserId(prevUserId => {
      if (prevUserId !== currentUserId) {
        return currentUserId;
      }
      return prevUserId;
    });

    setLoading(true);
    setError(null);

    try {
      // Auto-detect timezone for user limits
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const [limitsResponse, statusResponse] = await Promise.all([
        featureLimitsAPI.getUserLimits(currentUserId, userTimezone),
        featureLimitsAPI.getSubscriptionStatus(currentUserId)
      ]);

      setUserLimits(limitsResponse);
      setSubscriptionStatus(statusResponse);
    } catch (err) {
      setError(err.message);
      console.error('Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps - function always reads fresh from localStorage

  // Global refresh function for usage data
  const refreshFeatureUsage = useCallback(async () => {
    try {
      console.log('🔄 Refreshing feature usage data...');
      await loadUserData();
      console.log('✅ Feature usage data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing feature usage data:', error);
      // Don't throw the error, just log it
    }
  }, [loadUserData]);

  // Make refresh function globally available
  React.useEffect(() => {
    window.refreshFeatureUsage = refreshFeatureUsage;
    return () => {
      delete window.refreshFeatureUsage;
    };
  }, [refreshFeatureUsage]);

  // Listen for localStorage changes (UserID changes on login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'UserID') {
        const newUserId = e.newValue;
        
        // If UserID changed, reload data
        if (newUserId !== userId) {
          console.log('🔄 UserID changed in localStorage, refreshing limits...');
          setUserId(newUserId);
          // Small delay to ensure localStorage is fully updated
          setTimeout(() => {
            loadUserData();
          }, 100);
        }
      }
      
      // Also refresh when subscription-related fields change
      if (e.key === 'subscriptionEndDate' || e.key === 'SubscriptionType') {
        console.log('🔄 Subscription data changed, refreshing limits...');
        setTimeout(() => {
          loadUserData();
        }, 100);
      }
    };

    // Listen for storage events (works across tabs)
    window.addEventListener('storage', handleStorageChange);
    
    // Also poll for localStorage changes (works within same tab)
    // This is needed because storage events only fire in other tabs/windows
    const pollInterval = setInterval(() => {
      const currentUserId = localStorage.getItem('UserID');
      if (currentUserId !== userId) {
        console.log('🔄 UserID changed (detected via polling), refreshing limits...');
        setUserId(currentUserId);
        loadUserData();
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [userId, loadUserData]);

  // Check if user can perform a specific action
  const checkFeatureAccess = async (featureName, timezone = null, checkDate = null) => {
    if (!userId) return { canProceed: false, error: 'User not logged in' };

    try {
      // Auto-detect timezone if not provided
      const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const response = await featureLimitsAPI.checkFeatureAccess(userId, featureName, userTimezone, checkDate);
      return response;
    } catch (err) {
      console.error('Error checking feature access:', err);
      return { canProceed: false, error: err.message };
    }
  };

  // Log feature usage
  const logFeatureUsage = async (featureName, usageCount = 1, metadata = null, timezone = null) => {
    if (!userId) return false;

    try {
      // Auto-detect timezone if not provided
      const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      await featureLimitsAPI.logFeatureUsage(userId, featureName, usageCount, metadata, userTimezone);
      // Reload user data to update limits
      await loadUserData();
      return true;
    } catch (err) {
      console.error('Error logging feature usage:', err);
      return false;
    }
  };

  // Get limit for a specific feature
  const getFeatureLimit = (featureName) => {
    return userLimits.find(limit => limit.feature_name === featureName);
  };

  // Check if user is on free plan
  const isFreeUser = () => {
    return subscriptionStatus?.subscription_type === 'free';
  };

  // Check if user is a subscriber
  const isSubscriber = () => {
    return subscriptionStatus?.subscription_type === 'monthly' || 
           subscriptionStatus?.subscription_type === 'annually';
  };

  // Get subscription display name
  const getSubscriptionDisplayName = () => {
    if (!subscriptionStatus) return 'Unknown';
    
    const type = subscriptionStatus.subscription_type;
    const displayNames = {
      'free': 'Free',
      'monthly': 'Monthly',
      'annually': 'Annual'
    };
    
    return displayNames[type] || 'Unknown';
  };

  // Get remaining limit for a feature
  const getRemainingLimit = (featureName) => {
    const limit = getFeatureLimit(featureName);
    return limit ? limit.remaining_limit : 0;
  };

  // Check if user has reached limit for a feature
  const hasReachedLimit = (featureName) => {
    const limit = getFeatureLimit(featureName);
    return limit ? limit.remaining_limit <= 0 : true;
  };

  // Get usage percentage for a feature
  const getUsagePercentage = (featureName) => {
    const limit = getFeatureLimit(featureName);
    if (!limit || limit.limit_value === 0) return 0;
    
    const used = limit.limit_value - limit.remaining_limit;
    return Math.round((used / limit.limit_value) * 100);
  };

  // Load data on mount and when userId changes
  useEffect(() => {
    const currentUserId = localStorage.getItem('UserID');
    if (currentUserId) {
      loadUserData();
    }
  }, [userId, loadUserData]);

  const value = {
    userLimits,
    subscriptionStatus,
    loading,
    error,
    loadUserData,
    refreshFeatureLimits: refreshFeatureUsage, // Alias for refreshFeatureUsage
    checkFeatureAccess,
    logFeatureUsage,
    getFeatureLimit,
    isFreeUser,
    isSubscriber,
    getSubscriptionDisplayName,
    getRemainingLimit,
    hasReachedLimit,
    getUsagePercentage
  };

  return (
    <FeatureLimitsContext.Provider value={value}>
      {children}
    </FeatureLimitsContext.Provider>
  );
};
